// How much traffic each device made on one day, and which side made it.
//
// Backs the Message Box's fourth pane. Two server endpoints, both read-only:
//
//   GetMqttMessageCountForPeriodbyStartDateAndEndDate
//     -> one row per device: { UniqueNumber, DeviceName, RunCount,
//                              LocationName, RoomName, FullDetails, Date_time }
//        `RunCount` is the server's own aggregate — the authoritative total.
//
//   GetMqttMessageListForPeriodbyStartDateAndEndDate
//     -> the individual messages: { UniqueNumber, Message, Date_time, ... }
//        The only place the DIRECTION of each call is recorded.
//
// ⚠️ BOTH TAKE `StartDateTime` / `EndDateTime`, NOT StartDate / EndDate.
// Probed 2026-08-09: the short names give HTTP 400 on one endpoint and a
// 200-wrapped `{"status":"500"}` on the other. There is no error a caller would
// notice — the second one just looks like a day with no traffic.
//
// ⚠️ THE LIST IS CAPPED AT 500 ROWS. Measured:
//     one day    465 rows   against a RunCount total of 466   (complete)
//     9 days     500 rows   against 19,724                    (truncated 97%)
//     6 weeks    500 rows   against 29,584                    (truncated 98%)
// A single day fits; anything wider does not, and silently. That is why this
// module is DAY-SCOPED and why `truncated` is returned — the split has to be
// labelled approximate rather than quietly wrong.

import { getMqttMessageCounts, getMqttMessageList } from '../api/api';

/** The server's row cap on the list endpoint — measured, not documented. */
export const MESSAGE_LIST_CAP = 500;

const rows = (res) => (Array.isArray(res && res.data) ? res.data : []);
const pad = (n) => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' for a Date, in LOCAL time — the day the operator means. */
export const dayKey = (d = new Date()) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Which side sent a stored message.
 *
 * ⚠️ THE TEST IS "IS THE SUFFIX A USER ID", NOT "IS IT #D".
 *
 * `publishCommand` builds every client command as `[<uno>]<msg>#<userId>`, so a
 * client row ends in DIGITS. The device end has many markers — `#D`, `#Touch`,
 * `#BLE`, `#Boot`, `#Remote`, `#TwoWayAction`, `#T1308` — and they are not a
 * closed set: firmware builds invent their own. Matching a list of device
 * markers would therefore classify every new one as "client" the day it ships.
 * Matching digits cannot: a user id is always a number.
 *
 * Probed on live data (2026-08-09 and 07-08 Aug): every stored row ends `#D`.
 * There is not one client row on the whole estate, because outgoing logging is
 * a per-device switch and it is off nearly everywhere — see `SaveOutGoingMessages`.
 */
export function callSide(message) {
  const s = String(message ?? '');
  const i = s.lastIndexOf('#');
  if (i === -1) return 'unknown';
  const tail = s.slice(i + 1).trim();
  if (!tail) return 'unknown';
  return /^\d+$/.test(tail) ? 'client' : 'device';
}

/**
 * One day's per-device call counts.
 *
 * -> { date, rows: [...], truncated, listRows }
 *
 * Each row: { uniqueNumber, deviceName, locationName, roomName, owner,
 *             total, client, device, unknown }
 *
 * `total` is the server's own count. `client`/`device` are counted from the
 * message list, so they can add up to less than `total` if the cap was hit —
 * which is exactly what `truncated` reports.
 */
export async function fetchDayCallCounts(date) {
  const day = String(date || dayKey());
  const range = {
    StartDateTime: `${day} 00:00:00`,
    EndDateTime: `${day} 23:59:59`,
  };

  const [countRes, listRes] = await Promise.all([
    getMqttMessageCounts(range).catch(() => null),
    getMqttMessageList(range).catch(() => null),
  ]);

  const counts = rows(countRes);
  const list = rows(listRes);

  // uno -> { client, device, unknown }
  const bySide = new Map();
  list.forEach((m) => {
    const uno = String(m.UniqueNumber ?? '');
    if (!uno) return;
    if (!bySide.has(uno)) bySide.set(uno, { client: 0, device: 0, unknown: 0 });
    bySide.get(uno)[callSide(m.Message)] += 1;
  });

  const out = counts.map((r) => {
    const uno = String(r.UniqueNumber ?? '');
    const side = bySide.get(uno) || { client: 0, device: 0, unknown: 0 };
    return {
      uniqueNumber: uno,
      deviceName: r.DeviceName || uno,
      locationName: r.LocationName || '',
      roomName: r.RoomName || '',
      owner: r.FullDetails || '',
      total: Number(r.RunCount) || 0,
      ...side,
    };
  });

  // Busiest first: the question this pane answers is "what is chattering".
  out.sort((a, b) => b.total - a.total || a.deviceName.localeCompare(b.deviceName));

  return {
    date: day,
    rows: out,
    listRows: list.length,
    truncated: list.length >= MESSAGE_LIST_CAP,
  };
}

/** Totals across the day, for the pane's header. */
export function summarise(list) {
  return (list || []).reduce((acc, r) => ({
    devices: acc.devices + 1,
    total: acc.total + r.total,
    client: acc.client + r.client,
    device: acc.device + r.device,
  }), { devices: 0, total: 0, client: 0, device: 0 });
}
